import type { PumpPortalLiveRow } from "@/hooks/usePumpPortalTrades";
import { SCALPER_PAPER_CONFIG } from "@/lib/scalperPaperConfig";
import type { ScalperUserConfig } from "@/context/AppContext";

/**
 * Revival Hunter Lifecycle:
 *  watching  → Token is quiet, revival score is low.
 *  detected  → Revival score has crossed the threshold (e.g., > 1.5).
 *  arming    → Score is high AND we are waiting for a confirmation catalyst buy.
 *  in_trade  → Position open.
 */
export type RevivalHunterStatus = "watching" | "detected" | "arming" | "in_trade";

export type RevivalHunterSnapshot = {
  status: RevivalHunterStatus;
  currentRevivalScore: number;
  totalPnlPct: number;
  closedTrades: number;
  botTrades: any[]; // Simplified for now
  currentTrade: any | null;
};

export type ReduceRevivalHunterOpts = {
  minRevivalScore?: number;
  minLiquidityUsd?: number;
  scalperConfig?: ScalperUserConfig;
};

/**
 * Revival Hunter Engine
 * 
 * Instead of just watching dips, this engine looks for a surge in "Revival Score"
 * from the Nursery (calculated via volume acceleration and buy/sell ratios).
 */
export function reduceRevivalHunter(
  rows: PumpPortalLiveRow[],
  revivalScore: number,
  liquidityUsd: number,
  opts?: ReduceRevivalHunterOpts,
): RevivalHunterSnapshot {
  const minScore = opts?.minRevivalScore ?? 1.5;
  const minLiq = opts?.minLiquidityUsd ?? 1000;
  const C = opts?.scalperConfig ?? SCALPER_PAPER_CONFIG;

  let status: RevivalHunterStatus = "watching";
  
  // Logic: If liquidity is too low, we stay watching
  if (liquidityUsd < minLiq) {
    return {
      status: "watching",
      currentRevivalScore: revivalScore,
      totalPnlPct: 0,
      closedTrades: 0,
      botTrades: [],
      currentTrade: null,
    };
  }

  // If score is high, we've detected a move
  if (revivalScore >= minScore) {
    status = "detected";
    
    // Check if we have a recent catalyst buy in the last 10 prints to "Arm"
    const recentBuy = rows.slice(0, 10).some(r => r.buy && r.sol >= C.catalystMinSol);
    if (recentBuy) {
      status = "arming";
    }
  }

  return {
    status,
    currentRevivalScore: revivalScore,
    totalPnlPct: 0,
    closedTrades: 0,
    botTrades: [],
    currentTrade: null,
  };
}
